/**
 * useColdProject Hook
 *
 * Manages a single cold storage project with zones and optimization settings.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth, API_BASE_URL } from '@vortx/shared-ui';
import { useColdProjects } from './useColdProjects';
import { decomposeMeterLoad } from '../services/coldStorageService';

const DEFAULT_PROJECT_DATA = {
    zones: [],
    tariff_id: null,
};

/**
 * Compute a hash of zone config parameters that affect decomposition.
 * Used for staleness check — if zones change, cached decomposition is invalid.
 */
function computeZoneConfigHash(zones) {
    const str = JSON.stringify((zones || []).map(z => ({
        zone_id: z.id || z.zone_id,
        R: z.thermal_resistance_r,
        C: z.thermal_capacitance_c,
        COP: z.compressor?.cop,
        P_rated: z.compressor?.electrical_power_kw,
        temp_min: z.temp_min,
        temp_max: z.temp_max,
    })));
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
    }
    return hash.toString(16);
}

/**
 * Compute a deterministic fingerprint of the optimization inputs.
 * Used to detect when inputs change after results were generated.
 */
function computeInputsFingerprint(coldData, project) {
    const zones = (coldData?.zones || []).map(z => ({
        type: z.type,
        sqft: z.sqft,
        insulation_r_value: z.insulation_r_value,
        ceiling_height: z.ceiling_height,
        temp_min: z.temp_min,
        temp_max: z.temp_max,
        target_temp: z.target_temp,
        thermal_resistance_r: z.thermal_resistance_r,
        thermal_capacitance_c: z.thermal_capacitance_c,
        compressor: z.compressor,
    }));
    return JSON.stringify({
        zones,
        tariff_id: project?.meters?.main?.tariff?.id || null,
        lat: project?.location?.lat || null,
        lon: project?.location?.lon || null,
    });
}

export function useColdProject(projectId) {
    const { currentUser } = useAuth();
    const { projects, createProject, updateProject, deleteProject, loadProjects, loading: projectsLoading } = useColdProjects();
    const [project, setProject] = useState(null);
    const [saveStatus, setSaveStatus] = useState('idle'); // idle | dirty | saving | saved
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [error, setError] = useState(null);
    const [decomposing, setDecomposing] = useState(false);

    // Track which project we've already attempted a direct fetch for
    const directFetchedRef = useRef(null);

    // Find project from list.
    // If the project is not in the filtered list (e.g., navigated via URL for a
    // project that doesn't have flux_cold enabled yet), fall back to a direct fetch
    // and auto-enable the app.
    useEffect(() => {
        if (!projectId) {
            directFetchedRef.current = null;
            return;
        }

        if (projectsLoading) return;

        const found = projects.find(p => p.id === projectId);
        if (found) {
            setProject(found);
            setError(null);
            return;
        }

        // Not found in filtered list — try direct fetch (once per projectId).
        // Don't set error here — the async fetchAndEnable handles error states.
        // Setting error here would overwrite a successfully fetched project when
        // the effect re-runs after loadProjects().
        if (!currentUser || directFetchedRef.current === projectId) {
            return;
        }

        directFetchedRef.current = projectId;
        let cancelled = false;

        const fetchAndEnable = async () => {
            try {
                const token = await currentUser.getIdToken();

                // Fetch the project directly (no app filter)
                const res = await fetch(
                    `${API_BASE_URL}/api/projects/v1/projects/${projectId}`,
                    { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
                );

                if (!res.ok) {
                    if (!cancelled) {
                        setError('Project not found');
                    }
                    return;
                }

                const data = await res.json();
                const proj = data.project || data;

                // Auto-enable flux_cold if not already enabled
                if (!proj.apps?.flux_cold?.enabled) {
                    const enableRes = await fetch(
                        `${API_BASE_URL}/api/projects/v1/projects/${projectId}/apps/flux_cold`,
                        {
                            method: 'PUT',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ config: {} }),
                        }
                    );
                    if (enableRes.ok) {
                        const enableData = await enableRes.json();
                        const enabled = enableData.project || enableData;
                        if (!cancelled) {
                            setProject(enabled);
                            setError(null);
                            loadProjects();
                        }
                        return;
                    }
                }

                // Project exists and has flux_cold enabled (race with list load)
                if (!cancelled) {
                    setProject(proj);
                    setError(null);
                    loadProjects();
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to fetch project directly:', err);
                    setError('Project not found');
                }
            }
        };

        fetchAndEnable();

        return () => { cancelled = true; };
    }, [projectId, projects, projectsLoading, currentUser, loadProjects]);

    // Auto-save when project changes (debounced)
    useEffect(() => {
        if (!project || !projectId || saveStatus !== 'dirty') return;

        const timeout = setTimeout(async () => {
            try {
                setSaveStatus('saving');
                // Only send flux_cold app data, not all apps
                await updateProject(projectId, {
                    apps: {
                        flux_cold: project.apps.flux_cold
                    }
                });
                setSaveStatus('saved');
                setLastSavedAt(new Date().toISOString());
                setTimeout(() => setSaveStatus('idle'), 2000);
            } catch (err) {
                console.error('Failed to save project:', err);
                setError(err.message);
                setSaveStatus('idle');
            }
        }, 1000);

        return () => clearTimeout(timeout);
    }, [project, projectId, saveStatus, updateProject]);

    /**
     * Create a new project
     */
    const handleCreateProject = useCallback(async (name, description = '') => {
        try {
            const newProject = await createProject({
                name,
                description,
                location: {
                    address: '',
                    lat: 0,
                    lon: 0
                },
                apps: {
                    flux_cold: {
                        enabled: true,
                        config: DEFAULT_PROJECT_DATA
                    }
                }
            });
            return newProject;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [createProject]);

    /**
     * Delete current project
     */
    const handleDeleteProject = useCallback(async () => {
        if (!projectId) return;
        try {
            await deleteProject(projectId);
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [projectId, deleteProject]);

    /**
     * Update project field
     */
    const updateField = useCallback((field, value) => {
        setProject(prev => ({
            ...prev,
            apps: {
                ...prev.apps,
                flux_cold: {
                    ...prev.apps.flux_cold,
                    config: {
                        ...prev.apps.flux_cold.config,
                        [field]: value,
                    },
                },
            },
        }));
        setSaveStatus('dirty');
    }, []);

    /**
     * Update zones array
     */
    const updateZones = useCallback((zones) => {
        updateField('zones', zones);
    }, [updateField]);

    /**
     * Update load_relationship at apps.flux_cold level (not nested in config)
     */
    const updateLoadRelationship = useCallback(async (value) => {
        if (!projectId) return;
        try {
            setSaveStatus('saving');
            await updateProject(projectId, {
                apps: {
                    flux_cold: {
                        ...project.apps.flux_cold,
                        load_relationship: value,
                    }
                }
            });
            setProject(prev => ({
                ...prev,
                apps: {
                    ...prev.apps,
                    flux_cold: {
                        ...prev.apps.flux_cold,
                        load_relationship: value,
                    },
                },
            }));
            setSaveStatus('saved');
            setLastSavedAt(new Date().toISOString());
            setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (err) {
            console.error('Failed to update load relationship:', err);
            setError(err.message);
            setSaveStatus('idle');
        }
    }, [projectId, project, updateProject]);

    /**
     * Update meters at project level (not inside flux_cold config)
     */
    const updateMeters = useCallback(async (metersData) => {
        if (!projectId) return;
        try {
            setSaveStatus('saving');
            await updateProject(projectId, { meters: metersData });
            // Optimistically update local state
            setProject(prev => ({ ...prev, meters: metersData }));
            setSaveStatus('saved');
            setLastSavedAt(new Date().toISOString());
            setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (err) {
            console.error('Failed to update meters:', err);
            setError(err.message);
            setSaveStatus('idle');
        }
    }, [projectId, updateProject]);

    /**
     * Decompose the facility meter load into cold storage and residual components.
     * Calls the Flux COLD decompose API, stores results on meter.load_profile.decompositions.flux_cold,
     * and persists to Firestore. Includes zone_config_hash for staleness detection.
     */
    const handleDecompose = useCallback(async () => {
        if (!projectId || !project || !currentUser) return;

        const mainMeter = project.meters?.main;
        const loadProfile = mainMeter?.load_profile;
        if (!loadProfile?.loads_kw || loadProfile.loads_kw.length !== 8760) {
            throw new Error('Facility meter must have an 8760 hourly load profile to decompose');
        }

        const coldConfig = project.apps?.flux_cold?.config || {};
        const zones = coldConfig.zones || [];
        if (zones.length === 0) {
            throw new Error('At least one cold storage zone must be configured before decomposing');
        }

        const location = project.location || {};
        const loadProfileYear = loadProfile.year || new Date().getFullYear();

        setDecomposing(true);
        try {
            const result = await decomposeMeterLoad(
                loadProfile.loads_kw,
                { lat: location.lat || 0, lng: location.lon || location.lng || 0 },
                loadProfileYear,
                zones,
                currentUser
            );

            const zoneHash = computeZoneConfigHash(zones);

            // Store decomposition results on the load profile with zone hash
            const updatedLoadProfile = {
                ...loadProfile,
                decompositions: {
                    ...loadProfile.decompositions,
                    flux_cold: {
                        ...result,
                        zone_config_hash: zoneHash,
                    },
                },
            };

            const updatedMeters = {
                ...project.meters,
                main: {
                    ...mainMeter,
                    load_profile: updatedLoadProfile,
                },
            };

            // Persist to Firestore
            await updateProject(projectId, { meters: updatedMeters });

            // Update local state
            setProject(prev => ({
                ...prev,
                meters: updatedMeters,
            }));

            return result;
        } finally {
            setDecomposing(false);
        }
    }, [projectId, project, currentUser, updateProject]);

    // Auto-decompose: when project opens with valid meter load + zones,
    // check if cached decomposition is missing or stale, and trigger automatically.
    useEffect(() => {
        if (!project || !currentUser || decomposing) return;

        const meter = project.meters?.main;
        const coldConfig = project.apps?.flux_cold;
        const loadRel = coldConfig?.load_relationship || 'included';
        const zones = coldConfig?.config?.zones || [];
        const hasLoad = meter?.load_profile?.loads_kw?.length === 8760;
        const cached = meter?.load_profile?.decompositions?.flux_cold;

        if (loadRel !== 'included' || !hasLoad || zones.length === 0) return;

        // Check staleness: zone config changed since last decomposition
        const currentHash = computeZoneConfigHash(zones);
        if (cached && cached.zone_config_hash === currentHash) return; // cache is valid

        // Auto-trigger decomposition
        handleDecompose().catch(err => {
            console.error('Auto-decomposition failed:', err);
        });
    // Only run when the relevant project data changes, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        project?.meters?.main?.load_profile?.loads_kw,
        project?.apps?.flux_cold?.config?.zones,
        project?.apps?.flux_cold?.load_relationship,
        currentUser,
    ]);

    // Derived values — must be declared before callbacks that reference them
    const coldData = project?.apps?.flux_cold?.config || DEFAULT_PROJECT_DATA;
    const savedResults = project?.apps?.flux_cold?.last_results || null;
    const savedPeriod = project?.apps?.flux_cold?.last_run_period || null;
    const savedFingerprint = project?.apps?.flux_cold?.last_run_inputs_fingerprint || null;

    // Detect stale results: inputs changed since last optimization run
    const currentFingerprint = useMemo(
        () => computeInputsFingerprint(coldData, project),
        [coldData, project]
    );
    const resultsStale = !!savedResults && !!savedFingerprint && currentFingerprint !== savedFingerprint;

    /**
     * Save optimization results to Firestore (direct save, not via auto-save).
     * For annual results, strips raw arrays (load_curve_kw, temperature_trajectory)
     * to stay within Firestore's 1MB document limit. Only summaries are persisted.
     * Also saves an input fingerprint to detect stale results on reload.
     */
    const saveResults = useCallback(async (results, period) => {
        if (!projectId || !project) return;
        try {
            let persistedResults;
            if (results.type === 'annual') {
                // Build summary-only payload for Firestore
                persistedResults = {
                    type: 'annual',
                    annual_summary: results.annual_summary,
                    monthly_summaries: Object.fromEntries(
                        Object.entries(results.months || {}).map(([k, v]) => [k, {
                            status: v?.status,
                            energy_cost: v?.energy_cost,
                            demand_cost: v?.demand_cost,
                            peak_demand_kw: v?.peak_demand_kw,
                            savings_total: v?.savings?.total_cost || 0,
                            savings_pct: v?.savings?.total_cost_pct || 0,
                        }])
                    ),
                };
            } else {
                // Legacy single-month: save as-is
                persistedResults = results;
            }

            // Snapshot current inputs so we can detect stale results later
            const fingerprint = computeInputsFingerprint(coldData, project);

            const payload = {
                apps: {
                    flux_cold: {
                        ...project.apps.flux_cold,
                        last_results: persistedResults,
                        last_run_period: period,
                        last_run_at: new Date().toISOString(),
                        last_run_inputs_fingerprint: fingerprint,
                    }
                }
            };
            await updateProject(projectId, payload);
            setProject(prev => ({
                ...prev,
                apps: {
                    ...prev.apps,
                    flux_cold: {
                        ...prev.apps.flux_cold,
                        last_results: persistedResults,
                        last_run_period: period,
                        last_run_at: prev.apps.flux_cold.last_run_at || new Date().toISOString(),
                        last_run_inputs_fingerprint: fingerprint,
                    },
                },
            }));
        } catch (err) {
            console.error('Failed to save results:', err);
        }
    }, [projectId, project, coldData, updateProject]);

    return {
        project,
        coldData,
        savedResults,
        savedPeriod,
        resultsStale,
        loading: projectsLoading,
        saveStatus,
        lastSavedAt,
        error,
        createProject: handleCreateProject,
        deleteProject: handleDeleteProject,
        updateField,
        updateZones,
        updateMeters,
        updateLoadRelationship,
        saveResults,
        handleDecompose,
        decomposing,
    };
}
